import { IconChevronDown } from "@/assets/icons/geist/IconChevronDown";
import { IconCommit } from "@/assets/icons/geist/IconCommit";
import { IconFile } from "@/assets/icons/geist/IconFile";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/podkit/popover/Popover";
import { Text } from "@/components/podkit/typography/Text";
import { type PlainEnvironment } from "@/queries/environment-queries";
import { UncommitedFilesSection, UnpushedCommitsSection } from "@/routes/environments/details/GitStatusPanel";
import { gitStatusChangesCount, gitStatusLabel } from "@/utils/git-status";
import { type PlainMessage } from "@bufbuild/protobuf";
import { IconBranch } from "central-icons/IconBranch";
import type { EnvironmentGitStatus, FileChange } from "gitpod-next-api/gitpod/v1/environment_pb";
import { FileChange_ChangeType } from "gitpod-next-api/gitpod/v1/environment_pb";
import { type FC, type PropsWithChildren } from "react";

export const GitStatusTooltipContentFromGit: FC<{ git?: PlainMessage<EnvironmentGitStatus> }> = ({ git }) => {
    const label = gitStatusLabel(git);

    if (!git) {
        return null;
    }

    return (
        <div className="flex flex-col gap-1 gap-3">
            <div>
                <div className="font-bold">Git status</div>

                <p className="flex items-center gap-1 truncate text-sm text-content-secondary">
                    {label}
                    {git.branch && (
                        <>
                            {" on "}
                            <IconBranch className="size-3 text-content-secondary" />
                            <span className="text-content-secondary">{git.branch}</span>
                        </>
                    )}
                </p>
            </div>
            <div>
                <UnpushedCommitsSection gitStatus={git} />
                {git.changedFiles.length > 0 ? (
                    <>
                        <UncommitedFilesSection gitStatus={git} />
                    </>
                ) : null}
            </div>
        </div>
    );
};

export const GitStatusPopOver: FC<{ environment?: PlainEnvironment }> = ({ environment }) => {
    const changes = gitStatusChangesCount(environment?.status?.content?.git) ?? 0;
    const label = gitStatusLabel(environment?.status?.content?.git);
    const dirty = changes > 0;
    const disabled = changes === 0;

    if (!environment?.status?.content?.git) {
        return;
    }

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    className="group h-5 border-0 p-0 px-1 font-normal"
                    disabled={disabled}
                    aria-label="Git status"
                >
                    {label}
                    <IconChevronDown
                        size="sm"
                        className={cn(
                            "size-3 text-content-secondary",
                            dirty ? "group-hover:text-content-primary" : "text-content-tertiary",
                        )}
                    />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="z-10 flex w-[380px] flex-col gap-4 p-5 focus-visible:ring-0">
                <div>
                    <div className="font-bold">Git status</div>
                    <p className="text-base text-content-secondary">{label}</p>
                </div>
                <UnpushedCommits environment={environment} />
                <ChangedFiles environment={environment} />
            </PopoverContent>
        </Popover>
    );
};

export const ChangedFiles: FC<{ environment?: PlainEnvironment }> = ({ environment }) => {
    const files = environment?.status?.content?.git?.changedFiles || [];
    const totalCount = environment?.status?.content?.git?.totalChangedFiles || 0;
    const displayLimit = 15;
    const displayFiles = files.slice(0, displayLimit);
    const remainingCount = totalCount - displayLimit;

    return (
        <GitDetailsSection title="Uncomitted files" count={totalCount} data-testid="environment-details-changed-files">
            {displayFiles.map((file) => (
                <GitFileChange key={file.path} file={file} />
            ))}
            {remainingCount > 0 && <div className="mt-1 text-sm text-content-secondary">{remainingCount} more</div>}
        </GitDetailsSection>
    );
};

export const ChangedFilesFromGit: FC<{ git?: PlainMessage<EnvironmentGitStatus> }> = ({ git }) => {
    const files = git?.changedFiles || [];
    const totalCount = git?.totalChangedFiles || 0;
    const displayLimit = 15;
    const displayFiles = files.slice(0, displayLimit);
    const remainingCount = totalCount - displayLimit;

    return (
        <GitDetailsSection title="Uncomitted files" count={totalCount} data-testid="environment-details-changed-files">
            {displayFiles.map((file) => (
                <GitFileChange key={file.path} file={file} />
            ))}
            {remainingCount > 0 && <div className="mt-1 text-sm text-content-secondary">{remainingCount} more</div>}
        </GitDetailsSection>
    );
};

export const UnpushedCommits: FC<{ environment?: PlainEnvironment }> = ({ environment }) => {
    const commits = environment?.status?.content?.git?.unpushedCommits || [];
    const totalCount = environment?.status?.content?.git?.totalUnpushedCommits || 0;
    const displayLimit = 15;
    const displayCommits = commits.slice(0, displayLimit);
    const remainingCount = totalCount - displayLimit;

    return (
        <GitDetailsSection
            title="Unpushed commits"
            count={totalCount}
            data-testid="environment-details-unpushed-commits"
        >
            {displayCommits.map((commit) => (
                <GitCommit key={commit} commit={commit} />
            ))}
            {remainingCount > 0 && <div className="mt-1 text-sm text-content-secondary">{remainingCount} more</div>}
        </GitDetailsSection>
    );
};

export const UnpushedCommitsFromGit: FC<{ git?: PlainMessage<EnvironmentGitStatus> }> = ({ git }) => {
    const commits = git?.unpushedCommits || [];
    const totalCount = git?.totalUnpushedCommits || 0;
    const displayLimit = 15;
    const displayCommits = commits.slice(0, displayLimit);
    const remainingCount = totalCount - displayLimit;

    return (
        <GitDetailsSection
            title="Unpushed commits"
            count={totalCount}
            data-testid="environment-details-unpushed-commits"
        >
            {displayCommits.map((commit) => (
                <GitCommit key={commit} commit={commit} />
            ))}
            {remainingCount > 0 && <div className="mt-1 text-sm text-content-secondary">{remainingCount} more</div>}
        </GitDetailsSection>
    );
};

export const GitDetailsSection: FC<
    { title: string; count: number | undefined; "data-testid": string } & PropsWithChildren
> = ({ title, count, children, ...props }) => {
    if (!count) {
        return null;
    }
    return (
        <div className="flex flex-col" data-testid={props["data-testid"]}>
            <div className="flex justify-between">
                <Text className="mb-1 text-base">{title}</Text>
                <span className="text-base font-bold text-content-secondary">{count}</span>
            </div>
            <div className="flex flex-col gap-1">{children}</div>
        </div>
    );
};

export const GitCommit: FC<{ commit: string }> = ({ commit }) => {
    return (
        <div className="flex items-center gap-2">
            <IconCommit className="size-4 shrink-0" size="sm" />
            <span className="truncate text-sm text-content-secondary" title={commit}>
                {commit}
            </span>
        </div>
    );
};

export const GitFileChange: FC<{ file: PlainMessage<FileChange> }> = ({ file }) => {
    const parts = file.path.split("/");
    const path = parts.slice(0, parts.length - 1);
    const filename = parts[parts.length - 1];
    return (
        <div className="flex items-center gap-2">
            <IconFile className="size-4 shrink-0" size="sm" />
            <span className="max-w-[70%] shrink-0 truncate text-sm font-bold" title={filename}>
                {filename}
            </span>
            <span className="shrink truncate text-sm text-content-secondary" title={file.path}>
                {path.join("/")}
            </span>
            <GitChangeType changeType={file.changeType} />
        </div>
    );
};

/**
 * See https://git-scm.com/docs/git-status to reference the the letters
 */
const GitChangeType: FC<{ changeType: FileChange_ChangeType }> = ({ changeType }) => {
    let letter: React.ReactNode;
    switch (changeType) {
        case FileChange_ChangeType.UNSPECIFIED:
            letter = <span />;
            break;
        case FileChange_ChangeType.ADDED:
            letter = <span className="text-content-positive">A</span>;
            break;
        case FileChange_ChangeType.MODIFIED:
            letter = <span className="text-content-yield">M</span>;
            break;
        case FileChange_ChangeType.DELETED:
            letter = <span className="text-content-negative">D</span>;
            break;
        case FileChange_ChangeType.RENAMED:
            letter = <span className="text-content-positive">R</span>;
            break;
        case FileChange_ChangeType.COPIED:
            letter = <span className="text-content-positive">C</span>;
            break;
        case FileChange_ChangeType.UPDATED_BUT_UNMERGED:
            letter = <span className="text-content-negative">!</span>;
            break;
        case FileChange_ChangeType.UNTRACKED:
            letter = <span className="text-content-negative">U</span>;
            break;
    }

    return <div className="ml-auto h-5 w-9 text-right text-base">{letter}</div>;
};
